import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question } from '../../types';
import { getCitoQuestions, getQuestionWithExplanation } from '../../data/questionLoader';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { LatexRenderer } from '../ui/LatexRenderer';
import { Clock, CheckCircle, XCircle, ArrowRight, Trophy, Target, Zap, AlertCircle, BarChart3, Timer, ChevronDown, ChevronUp, Brain, TrendingUp, Award } from 'lucide-react';
interface CitoSimulatorProps {
  onExit: () => void;
  onAnswer: (questionId: string, isCorrect: boolean, confidence: number, userChoice: number) => void;
}
interface Answer {
  questionId: string;
  userChoice: number;
  isCorrect: boolean;
  timeSpent: number;
  difficulty: number;
  module: string;
}
type SimulatorView = 'intro' | 'test' | 'review';
export function CitoSimulator({
  onExit,
  onAnswer
}: CitoSimulatorProps) {
  const [view, setView] = useState<SimulatorView>('intro');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(45 * 60); // 45 minuten
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  // Generate 30 questions
  const [citoQuestions] = useState(() => getCitoQuestions(30));
  const currentQuestion = citoQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === citoQuestions.length - 1;
  // Timer Logic
  useEffect(() => {
    if (view !== 'test') return;
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleFinishTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [view]);
  // Reset question timer
  useEffect(() => {
    if (view === 'test') {
      setQuestionStartTime(Date.now());
      setSelectedAnswer(null);
    }
  }, [currentQuestionIndex, view]);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const handleStartTest = () => {
    setView('test');
    setTimeRemaining(45 * 60);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setQuestionStartTime(Date.now());
  };
  const handleAnswerSelect = (choiceIndex: number) => {
    setSelectedAnswer(choiceIndex);
  };
  const handleNextQuestion = () => {
    if (selectedAnswer === null) return;
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    const newAnswer: Answer = {
      questionId: currentQuestion.id,
      userChoice: selectedAnswer,
      isCorrect,
      timeSpent,
      difficulty: currentQuestion.difficulty || 1,
      module: currentQuestion.module
    };
    setAnswers([...answers, newAnswer]);
    onAnswer(currentQuestion.id, isCorrect, 3, selectedAnswer);
    if (isLastQuestion) {
      handleFinishTest();
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
    }
  };
  const handleFinishTest = () => {
    setView('review');
  };
  // --- ANALYTICS CALCULATIONS ---
  const stats = useMemo(() => {
    if (answers.length === 0) return null;
    const correctCount = answers.filter(a => a.isCorrect).length;
    const incorrectCount = answers.length - correctCount;
    const percentage = Math.round(correctCount / answers.length * 100);
    const totalTimeSpent = answers.reduce((acc, curr) => acc + curr.timeSpent, 0);
    const avgTimePerQuestion = Math.round(totalTimeSpent / answers.length);
    // Difficulty Analysis
    const difficultyStats = {
      1: {
        total: 0,
        correct: 0
      },
      2: {
        total: 0,
        correct: 0
      },
      3: {
        total: 0,
        correct: 0
      }
    };
    answers.forEach(a => {
      const diff = a.difficulty as 1 | 2 | 3;
      if (difficultyStats[diff]) {
        difficultyStats[diff].total++;
        if (a.isCorrect) difficultyStats[diff].correct++;
      }
    });
    // Cito-style Grade Estimate (Rough approximation)
    let grade = 'III';
    let gradeColor = 'text-orange-600';
    let gradeText = 'Matig';
    if (percentage >= 90) {
      grade = 'I+';
      gradeColor = 'text-emerald-600';
      gradeText = 'Uitmuntend';
    } else if (percentage >= 80) {
      grade = 'I';
      gradeColor = 'text-emerald-500';
      gradeText = 'Zeer Goed';
    } else if (percentage >= 70) {
      grade = 'II';
      gradeColor = 'text-blue-600';
      gradeText = 'Goed';
    } else if (percentage >= 55) {
      grade = 'III';
      gradeColor = 'text-yellow-600';
      gradeText = 'Voldoende';
    } else if (percentage >= 40) {
      grade = 'IV';
      gradeColor = 'text-orange-600';
      gradeText = 'Matig';
    } else {
      grade = 'V';
      gradeColor = 'text-red-600';
      gradeText = 'Onvoldoende';
    }
    return {
      correctCount,
      incorrectCount,
      percentage,
      totalTimeSpent,
      avgTimePerQuestion,
      difficultyStats,
      grade,
      gradeColor,
      gradeText
    };
  }, [answers]);
  // --- VIEWS ---
  if (view === 'intro') {
    return <div className="min-h-screen bg-slate-50 p-4 md:p-8 flex items-center justify-center" data-id="element-95">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full max-w-3xl" data-id="element-96">
          <Card className="overflow-hidden border-0 shadow-xl" data-id="element-97">
            {/* Header Banner */}
            <div className="bg-slate-900 p-8 text-white relative overflow-hidden" data-id="element-98">
              <div className="absolute top-0 right-0 p-8 opacity-10" data-id="element-99">
                <Brain className="w-64 h-64 transform translate-x-12 -translate-y-12" data-id="element-100" />
              </div>
              <div className="relative z-10" data-id="element-101">
                <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-sm font-medium mb-4 backdrop-blur-sm border border-white/20" data-id="element-102">
                  <Target className="w-4 h-4" data-id="element-103" />
                  <span data-id="element-104">Officiële Oefenmodus</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-105">
                  Cito Oefentoets
                </h1>
                <p className="text-slate-300 text-lg max-w-xl" data-id="element-106">
                  Test je kennis met een realistische simulatie. 30 vragen uit
                  alle domeinen, precies zoals op de echte toets.
                </p>
              </div>
            </div>

            <div className="p-8" data-id="element-107">
              <div className="grid md:grid-cols-3 gap-6 mb-8" data-id="element-108">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-id="element-109">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3" data-id="element-110">
                    <Brain className="w-6 h-6 text-blue-600" data-id="element-111" />
                  </div>
                  <h3 className="font-bold text-slate-900" data-id="element-112">30 Vragen</h3>
                  <p className="text-sm text-slate-600" data-id="element-113">
                    Mix van rekenen, taal en spelling
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-id="element-114">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-3" data-id="element-115">
                    <Clock className="w-6 h-6 text-orange-600" data-id="element-116" />
                  </div>
                  <h3 className="font-bold text-slate-900" data-id="element-117">45 Minuten</h3>
                  <p className="text-sm text-slate-600" data-id="element-118">
                    Gemiddeld 1.5 minuut per vraag
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-id="element-119">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-3" data-id="element-120">
                    <BarChart3 className="w-6 h-6 text-emerald-600" data-id="element-121" />
                  </div>
                  <h3 className="font-bold text-slate-900" data-id="element-122">Analyse</h3>
                  <p className="text-sm text-slate-600" data-id="element-123">
                    Uitgebreid rapport na afloop
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-8 flex gap-3" data-id="element-124">
                <AlertCircle className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" data-id="element-125" />
                <div className="text-sm text-yellow-800" data-id="element-126">
                  <p className="font-bold mb-1" data-id="element-127">Belangrijk voor je start:</p>
                  <ul className="list-disc list-inside space-y-1 ml-1" data-id="element-128">
                    <li data-id="element-129">Je kunt niet terug naar vorige vragen</li>
                    <li data-id="element-130">Gebruik geen rekenmachine</li>
                    <li data-id="element-131">Kladpapier mag wel!</li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3" data-id="element-132">
                <Button onClick={handleStartTest} variant="primary" size="lg" className="flex-1 text-lg h-14" data-id="element-133">
                  Start de Toets
                  <ArrowRight className="w-5 h-5 ml-2" data-id="element-134" />
                </Button>
                <Button onClick={onExit} variant="secondary" size="lg" className="h-14" data-id="element-135">
                  Terug
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>;
  }
  if (view === 'test') {
    const progress = (currentQuestionIndex + 1) / citoQuestions.length * 100;
    return <div className="min-h-screen bg-slate-100 flex flex-col" data-id="element-136">
        {/* Professional Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm" data-id="element-137">
          <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between" data-id="element-138">
            <div className="flex items-center gap-4" data-id="element-139">
              <div className="font-bold text-slate-900 text-lg" data-id="element-140">
                Cito Oefentoets
              </div>
              <div className="hidden md:block h-6 w-px bg-slate-200" data-id="element-141"></div>
              <div className="text-slate-500 text-sm font-medium" data-id="element-142">
                Vraag {currentQuestionIndex + 1}{' '}
                <span className="text-slate-300" data-id="element-143">/</span> {citoQuestions.length}
              </div>
            </div>

            <div className={`flex items-center gap-2 font-mono font-medium px-3 py-1.5 rounded-md ${timeRemaining < 300 ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-700'}`} data-id="element-144">
              <Clock className="w-4 h-4" data-id="element-145" />
              {formatTime(timeRemaining)}
            </div>
          </div>
          {/* Progress Bar */}
          <div className="h-1 bg-slate-100 w-full" data-id="element-146">
            <motion.div className="h-full bg-blue-600" initial={{
            width: 0
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.3
          }} data-id="element-147" />
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 max-w-3xl w-full mx-auto p-4 md:p-8 flex flex-col justify-center" data-id="element-148">
          <AnimatePresence mode="wait" data-id="element-149">
            <motion.div key={currentQuestionIndex} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.2
          }} data-id="element-150">
              <Card className="p-6 md:p-10 shadow-sm border-slate-200" data-id="element-151">
                {/* Context / Story */}
                {currentQuestion.context && <div className="mb-8 p-6 bg-slate-50 rounded-xl border border-slate-200 text-slate-700 text-lg leading-relaxed font-serif" data-id="element-152">
                    <LatexRenderer content={currentQuestion.context} data-id="element-153" />
                  </div>}

                {/* Question */}
                <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-8 leading-snug" data-id="element-154">
                  <LatexRenderer content={currentQuestion.question} data-id="element-155" />
                </h2>

                {/* Options */}
                <div className="space-y-3" data-id="element-156">
                  {currentQuestion.options.map((option, index) => <button key={index} onClick={() => handleAnswerSelect(index)} className={`w-full text-left p-4 md:p-5 rounded-xl border-2 transition-all duration-200 flex items-center gap-4 group ${selectedAnswer === index ? 'border-blue-600 bg-blue-50 shadow-sm' : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'}`} data-id="element-157">
                      <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold transition-colors ${selectedAnswer === index ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300 text-slate-400 group-hover:border-blue-400 group-hover:text-blue-500'}`} data-id="element-158">
                        {String.fromCharCode(65 + index)}
                      </div>
                      <div className="text-lg text-slate-800" data-id="element-159">
                        <LatexRenderer content={option} data-id="element-160" />
                      </div>
                    </button>)}
                </div>
              </Card>
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Footer Actions */}
        <footer className="bg-white border-t border-slate-200 p-4" data-id="element-161">
          <div className="max-w-3xl mx-auto flex justify-end" data-id="element-162">
            <Button onClick={handleNextQuestion} disabled={selectedAnswer === null} variant="primary" size="lg" className="w-full md:w-auto min-w-[200px]" data-id="element-163">
              {isLastQuestion ? 'Toets Afronden' : 'Volgende Vraag'}
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-164" />
            </Button>
          </div>
        </footer>
      </div>;
  }
  if (view === 'review' && stats) {
    return <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-id="element-165">
        <div className="max-w-5xl mx-auto space-y-6" data-id="element-166">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-167">
            <div data-id="element-168">
              <h1 className="text-3xl font-bold text-slate-900" data-id="element-169">
                Resultaten Analyse
              </h1>
              <p className="text-slate-500" data-id="element-170">
                Gedetailleerd overzicht van je prestaties
              </p>
            </div>
            <Button onClick={onExit} variant="secondary" data-id="element-171">
              Terug naar Dashboard
            </Button>
          </div>

          {/* Top Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-172">
            {/* Main Score Card */}
            <Card className="md:col-span-2 p-6 bg-white border-l-4 border-l-blue-600" data-id="element-173">
              <div className="flex items-center justify-between mb-4" data-id="element-174">
                <h3 className="font-bold text-slate-700 flex items-center gap-2" data-id="element-175">
                  <Trophy className="w-5 h-5 text-blue-600" data-id="element-176" />
                  Eindscore
                </h3>
                <span className={`text-sm font-bold px-2 py-1 rounded bg-slate-100 ${stats.gradeColor}`} data-id="element-177">
                  Niveau {stats.grade}
                </span>
              </div>
              <div className="flex items-end gap-4" data-id="element-178">
                <div className="text-5xl font-bold text-slate-900" data-id="element-179">
                  {stats.percentage}%
                </div>
                <div className={`text-lg font-medium mb-2 ${stats.gradeColor}`} data-id="element-180">
                  {stats.gradeText}
                </div>
              </div>
              <div className="mt-4 w-full bg-slate-100 h-2 rounded-full overflow-hidden" data-id="element-181">
                <div className="h-full bg-blue-600 rounded-full" style={{
                width: `${stats.percentage}%`
              }} data-id="element-182" />
              </div>
              <p className="text-sm text-slate-500 mt-2" data-id="element-183">
                {stats.correctCount} van de {answers.length} vragen goed
                beantwoord
              </p>
            </Card>

            {/* Time Stats */}
            <Card className="p-6" data-id="element-184">
              <h3 className="font-bold text-slate-700 flex items-center gap-2 mb-4" data-id="element-185">
                <Timer className="w-5 h-5 text-orange-600" data-id="element-186" />
                Tijd
              </h3>
              <div className="space-y-4" data-id="element-187">
                <div data-id="element-188">
                  <div className="text-2xl font-bold text-slate-900" data-id="element-189">
                    {Math.floor(stats.totalTimeSpent / 60)}m{' '}
                    {stats.totalTimeSpent % 60}s
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-190">Totale tijd</div>
                </div>
                <div data-id="element-191">
                  <div className="text-xl font-bold text-slate-900" data-id="element-192">
                    {stats.avgTimePerQuestion}s
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-193">
                    Gemiddeld per vraag
                  </div>
                </div>
              </div>
            </Card>

            {/* Focus Areas */}
            <Card className="p-6" data-id="element-194">
              <h3 className="font-bold text-slate-700 flex items-center gap-2 mb-4" data-id="element-195">
                <TrendingUp className="w-5 h-5 text-emerald-600" data-id="element-196" />
                Niveau
              </h3>
              <div className="space-y-3" data-id="element-197">
                {[1, 2, 3].map(level => {
                const s = stats.difficultyStats[level as 1 | 2 | 3];
                if (s.total === 0) return null;
                const p = Math.round(s.correct / s.total * 100);
                return <div key={level} className="flex items-center justify-between text-sm" data-id="element-198">
                      <span className="text-slate-600" data-id="element-199">Niveau {level}</span>
                      <div className="flex items-center gap-2" data-id="element-200">
                        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-201">
                          <div className={`h-full rounded-full ${p >= 80 ? 'bg-emerald-500' : p >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                        width: `${p}%`
                      }} data-id="element-202" />
                        </div>
                        <span className="font-bold w-8 text-right" data-id="element-203">{p}%</span>
                      </div>
                    </div>;
              })}
              </div>
            </Card>
          </div>

          {/* Detailed Question Review */}
          <div className="space-y-4" data-id="element-204">
            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4" data-id="element-205">
              Vragen Review
            </h2>
            {answers.map((answer, idx) => <ReviewItem key={idx} answer={answer} index={idx} data-id="element-206" />)}
          </div>

          <div className="flex justify-center pt-8 pb-12" data-id="element-207">
            <Button onClick={onExit} variant="primary" size="lg" className="min-w-[200px]" data-id="element-208">
              Terug naar Dashboard
            </Button>
          </div>
        </div>
      </div>;
  }
  return null;
}
// Separate component for review items to handle expanded state cleanly
function ReviewItem({
  answer,
  index
}: {
  answer: Answer;
  index: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const question = getQuestionWithExplanation(answer.questionId);
  if (!question) return null;
  return <Card className={`transition-all duration-200 border-l-4 ${answer.isCorrect ? 'border-l-emerald-500' : 'border-l-red-500'}`} data-id="element-209">
      <div onClick={() => setIsExpanded(!isExpanded)} className="p-4 flex items-center gap-4 cursor-pointer hover:bg-slate-50" data-id="element-210">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${answer.isCorrect ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`} data-id="element-211">
          {answer.isCorrect ? <CheckCircle className="w-5 h-5" data-id="element-212" /> : <XCircle className="w-5 h-5" data-id="element-213" />}
        </div>

        <div className="flex-1 min-w-0" data-id="element-214">
          <div className="flex items-center gap-2 mb-1" data-id="element-215">
            <span className="font-bold text-slate-700 text-sm" data-id="element-216">
              Vraag {index + 1}
            </span>
            <span className="text-xs text-slate-400" data-id="element-217">•</span>
            <span className="text-xs text-slate-500" data-id="element-218">{answer.module}</span>
            <span className="text-xs text-slate-400" data-id="element-219">•</span>
            <span className="text-xs text-slate-500" data-id="element-220">{answer.timeSpent}s</span>
          </div>
          <p className="text-slate-900 font-medium truncate pr-4" data-id="element-221">
            <LatexRenderer content={question.question} data-id="element-222" />
          </p>
        </div>

        <div className="text-slate-400" data-id="element-223">
          {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-224" /> : <ChevronDown className="w-5 h-5" data-id="element-225" />}
        </div>
      </div>

      <AnimatePresence data-id="element-226">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-227">
            <div className="p-4 pt-0 border-t border-slate-100 bg-slate-50/50" data-id="element-228">
              <div className="mt-4 space-y-4" data-id="element-229">
                {/* Full Question Context if exists */}
                {question.context && <div className="p-3 bg-white rounded border border-slate-200 text-sm text-slate-600 italic" data-id="element-230">
                    <LatexRenderer content={question.context} data-id="element-231" />
                  </div>}

                {/* Full Question */}
                <div className="text-lg font-medium text-slate-900" data-id="element-232">
                  <LatexRenderer content={question.question} data-id="element-233" />
                </div>

                {/* Answers Comparison */}
                <div className="grid md:grid-cols-2 gap-4" data-id="element-234">
                  <div className={`p-3 rounded-lg border ${answer.isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-id="element-235">
                    <div className="text-xs font-bold uppercase mb-1 opacity-70" data-id="element-236">
                      Jouw Antwoord
                    </div>
                    <div className="font-medium" data-id="element-237">
                      <LatexRenderer content={question.options[answer.userChoice]} data-id="element-238" />
                    </div>
                  </div>

                  {!answer.isCorrect && <div className="p-3 rounded-lg border bg-emerald-50 border-emerald-200" data-id="element-239">
                      <div className="text-xs font-bold uppercase text-emerald-800 mb-1 opacity-70" data-id="element-240">
                        Juiste Antwoord
                      </div>
                      <div className="font-medium text-emerald-900" data-id="element-241">
                        <LatexRenderer content={question.options[question.correctAnswer]} data-id="element-242" />
                      </div>
                    </div>}
                </div>

                {/* Explanation */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-243">
                  <div className="flex items-center gap-2 font-bold text-blue-800 mb-2" data-id="element-244">
                    <Zap className="w-4 h-4" data-id="element-245" />
                    Uitleg
                  </div>
                  <div className="text-blue-900 leading-relaxed" data-id="element-246">
                    <LatexRenderer content={question.explanation} data-id="element-247" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </Card>;
}